import { useEffect, useRef, useCallback, useState } from 'react';
import { getAuthToken } from '../lib/api';

export interface NotificationMessage {
  type: 'job_status_change';
  job_id: string;
  status: 'queued' | 'processing' | 'completed' | 'retrying' | 'permanently_failed';
  thumbnail_url?: string;
  error_message?: string;
  timestamp: string;
}

interface UseWebSocketOptions {
  onMessage?: (msg: NotificationMessage) => void;
  enabled?: boolean;
}

export function useWebSocket({ onMessage, enabled = true }: UseWebSocketOptions = {}) {
  const wsRef = useRef<WebSocket | null>(null);
  const retriesRef = useRef(0);
  const maxRetries = 3;
  const [connected, setConnected] = useState(false);
  const [fallbackToPolling, setFallbackToPolling] = useState(false);

  const connect = useCallback(() => {
    const token = getAuthToken();
    if (!token || !enabled) return;

    const apiUrl = import.meta.env.VITE_API_URL || 'https://api.cfai.work';
    const wsHost = apiUrl.replace(/^https?:\/\//, '');
    const protocol = apiUrl.startsWith('https') ? 'wss:' : 'ws:';
    const wsUrl = `${protocol}//${wsHost}/v1/ws/notifications?token=${encodeURIComponent(token)}`;

    try {
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        setConnected(true);
        retriesRef.current = 0;
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data) as NotificationMessage;
          onMessage?.(data);
        } catch {
          // Ignore malformed messages
        }
      };

      ws.onclose = () => {
        setConnected(false);
        wsRef.current = null;

        if (retriesRef.current < maxRetries) {
          const delay = Math.pow(2, retriesRef.current) * 1000;
          retriesRef.current++;
          setTimeout(connect, delay);
        } else {
          setFallbackToPolling(true);
        }
      };

      ws.onerror = () => {
        ws.close();
      };
    } catch {
      setFallbackToPolling(true);
    }
  }, [enabled, onMessage]);

  useEffect(() => {
    if (enabled) {
      connect();
    }

    return () => {
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
    };
  }, [connect, enabled]);

  return { connected, fallbackToPolling };
}
